import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Rectangle, useMap } from 'react-leaflet';
import { themeColor, useThemeMode } from './theme';

const DEFAULT_CENTER = [50.0, 14.4];

// Site "within boundaries" picker: drag a lat-lon rectangle on the map;
// onApply receives [west, south, east, north].
export default function MapBoundsDialog({ title, sites, bounds, onApply, onClose }) {
  useThemeMode();
  const [rect, setRect] = useState(() => readBounds(bounds));
  const [drawMode, setDrawMode] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const positioned = (sites || []).filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));
  const color = themeColor('--md-sys-color-primary', '#8b4513');

  return (
    <div
      className="dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="dialog dialog-map" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 className="dialog-title">{title}</h2>
          <button type="button" className="dialog-close" onClick={onClose} title="Close">
            ×
          </button>
        </div>
        <div className="dialog-body">
          <p className="dialog-hint">
            Drag on the map to draw a region; only sites inside the rectangle are kept.
          </p>
          <MapContainer
            center={rect ? centerOf(rect) : DEFAULT_CENTER}
            zoom={4}
            className="bounds-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {positioned.map((s) => (
              <Marker key={s.value} position={[s.lat, s.lon]} interactive={false} />
            ))}
            {rect && (
              <Rectangle
                bounds={[
                  [rect.south, rect.west],
                  [rect.north, rect.east]
                ]}
                pathOptions={{ color, weight: 1.5, fillOpacity: 0.12 }}
              />
            )}
            <MapController
              drawMode={drawMode}
              color={color}
              onRect={(r) => {
                setRect(r);
                setDrawMode(false);
              }}
            />
          </MapContainer>
          <div className="dialog-tools">
            <button
              type="button"
              className={`btn ${drawMode ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDrawMode(!drawMode)}
            >
              {drawMode ? 'Draw… (drag on the map)' : 'Draw region'}
            </button>
            {rect && (
              <button type="button" className="btn btn-secondary" onClick={() => setRect(null)}>
                Clear
              </button>
            )}
            <span className="dialog-count">
              {rect
                ? `${positioned.filter((s) => insideRect(s, rect)).length} of ${
                    positioned.length
                  } sites inside`
                : 'No region selected'}
            </span>
          </div>
        </div>
        <div className="dialog-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onApply(rect ? [rect.west, rect.south, rect.east, rect.north] : [])}
          >
            Select region
          </button>
        </div>
      </div>
    </div>
  );
}

// Draw mode: panning off; mousedown→mouseup becomes the rectangle (dashed draft).
function MapController({ drawMode, onRect, color }) {
  const map = useMap();
  const origin = useRef(null);
  const [draft, setDraft] = useState(null);
  const applyRef = useRef(onRect);
  applyRef.current = onRect;

  useEffect(() => {
    if (drawMode) map.dragging.disable();
    else map.dragging.enable();
    if (!drawMode) return undefined;

    const down = (e) => {
      origin.current = e.latlng;
      setDraft(null);
    };
    const move = (e) => {
      if (!origin.current) return;
      setDraft(rectFrom(origin.current, map.mouseEventToLatLng(e)));
    };
    const up = (e) => {
      const start = origin.current;
      origin.current = null;
      setDraft(null);
      if (start) {
        const r = rectFrom(start, map.mouseEventToLatLng(e));
        if (r) applyRef.current(r);
      }
    };
    map.on('mousedown', down);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    map.getContainer().classList.add('bounds-drawing');
    return () => {
      map.off('mousedown', down);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      map.getContainer().classList.remove('bounds-drawing');
    };
  }, [map, drawMode]);

  return draft ? (
    <Rectangle
      bounds={[
        [draft.south, draft.west],
        [draft.north, draft.east]
      ]}
      pathOptions={{ color, weight: 1.5, dashArray: '6 6', fillOpacity: 0.12 }}
    />
  ) : null;
}

function centerOf(rect) {
  return [(rect.south + rect.north) / 2, (rect.west + rect.east) / 2];
}

function insideRect(site, rect) {
  return (
    site.lat >= rect.south &&
    site.lat <= rect.north &&
    site.lon >= rect.west &&
    site.lon <= rect.east
  );
}

function readBounds(bounds) {
  const b = (bounds || []).map(Number);
  if (b.length !== 4 || b.some((v) => !Number.isFinite(v))) return null;
  return { west: b[0], south: b[1], east: b[2], north: b[3] };
}

function rectFrom(a, b) {
  const west = Math.min(a.lng, b.lng);
  const east = Math.max(a.lng, b.lng);
  const south = Math.min(a.lat, b.lat);
  const north = Math.max(a.lat, b.lat);
  if (north - south < 1e-6 || east - west < 1e-6) return null;
  return { west, south, east, north };
}
